import { ExclamationTriangleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { cn } from "@/lib/utils";
import { theme } from "@/lib/theme";
import type { AskSession } from "@/api/client";
import type { Podcast } from "@/hooks/usePodcasts";

interface DeleteSessionWarningModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  sessionToDelete: AskSession | null;
  podcast: Podcast | null;
  isCreating: boolean;
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins} minutes ago`;
  if (diffHours < 24) return `${diffHours} hours ago`;
  return `${Math.floor(diffHours / 24)} days ago`;
}

export default function DeleteSessionWarningModal({
  isOpen,
  onClose,
  onConfirm,
  sessionToDelete,
  podcast,
  isCreating,
}: DeleteSessionWarningModalProps) {
  if (!isOpen || !sessionToDelete) return null;

  const snippet = sessionToDelete.first_question_snippet || "New conversation";
  const messageCount = sessionToDelete.message_count ?? 0;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-50"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className={cn(
            "w-full max-w-md",
            theme.cards.base,
            "p-6",
          )}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-start gap-4 mb-4">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
              <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                Session Limit Reached
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                You can have up to 3 active chat sessions. Creating a new one will delete your oldest session.
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Session to delete preview */}
          <div className={cn(
            "p-4 rounded-lg mb-6",
            "bg-gray-50 dark:bg-gray-800/50",
            "border-2 border-gray-200 dark:border-gray-700",
          )}>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">{podcast?.emoji ?? "🎧"}</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {podcast?.name ?? sessionToDelete.podcast_slug}
              </span>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-2 line-clamp-2">
              "{snippet}"
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {messageCount} {messageCount === 1 ? "message" : "messages"} · Last active {formatRelativeTime(sessionToDelete.last_activity_at)}
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={isCreating}
              className={cn(
                "flex-1 px-4 py-2.5 rounded-lg font-medium",
                theme.buttons.secondary,
                "disabled:opacity-50 disabled:cursor-not-allowed",
              )}
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={isCreating}
              className={cn(
                "flex-1 px-4 py-2.5 rounded-lg font-medium",
                theme.buttons.primary,
                "disabled:opacity-50 disabled:cursor-not-allowed",
              )}
            >
              {isCreating ? "Creating..." : "Create Anyway"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
